// @flow
/**
 * Client
 * @module Client
 */
import apisauce from 'apisauce';
import axios from 'axios';
import config from 'config';
import { ActionTypes } from 'constants/index';
import consumptionClient from 'modules/consumptionClient';
import { push } from 'modules/history';

const create = (baseURL = config.BASE_URL) => {
  const customAxiosInstance = axios.create({ baseURL });

  const api = apisauce.create({
    axiosInstance: customAxiosInstance,
  });

  const consumptionApi = consumptionClient.create();

  api.addResponseTransform(response => {
    const newJwt =
      response.headers && (response.headers['x-new-jwt'] || response.headers['X-New-Jwt']);
    if (newJwt && response.status && response.status >= 200 && response.status < 300) {
      api.setHeader('jwt', newJwt);
      if (typeof global !== 'undefined' && global.store) {
        global.store.dispatch({
          type: ActionTypes.AUTH_TOKEN_REFRESHED,
          payload: { token: newJwt },
        });
      }
    }

    if (response.status === null || response.status === 500) {
      response.data = { errors: { message: 'Network error' } };
      response.status = 500;
      push('/internal-server-error');
    }

    if (response.status === 403) {
      response.data = { errors: { message: 'You are not authorized to access this resource' } };
      response.status = 403;
      push('/unauthorized-error');
    }
    if (response.status === 401) {
      response.data = { errors: { message: 'Session expired, please log in again' } };
      response.status = 401;
      // Do NOT clear the token or dispatch a logout here. This transform runs for
      // every parallel 401, and clearing `jwt` here (plus the persisted
      // `token: null`) locked users out until they manually cleared their browser
      // cache. The /expired-token screen's "Go login" link dispatches the logout.
      push('/expired-token');
    }
  });

  // api
  const setAuthorization = token => api.setHeader('jwt', token);
  const removeAuthToken = () => api.deleteHeader('jwt');

  // login
  const localLogin = data => api.post('/sign-in', data);

  const recoverPasswordRequest = data => api.post('/recover-password', { email: data });

  const resetPasswordRequest = data => api.post('/reset-password', data);

  const newPasswordRequest = data => api.post('/new-password', data);

  const resendInvitation = data => api.post('/resend-invitation', data);

  // profile
  const updateProfile = params => api.put('/users/me', params);

  const changePassword = params => api.put('/users/change-password', params);

  const closeAccount = () => api.delete('/users/me');

  // search
  const getSearchSuggestions = params => api.get('/search/suggest', params);

  // location
  const getLocations = params => api.get('/locations', params);

  const getLocation = id => api.get(`/locations/${id}`);

  const getLocationsForFilter = params => api.get('/locations/filter', params);

  const createLocation = data => api.post('/locations', data);
  const uploadLocationImport = data => api.post('/location-imports/upload', data);
  const getLocationImports = params => api.get('/location-imports', params);
  const getLocationImport = (id, params) => api.get(`/location-imports/${id}`, params);
  const updateLocationImportRow = (importId, rowId, data) =>
    api.patch(`/location-imports/${importId}/rows/${rowId}`, data);
  const approveLocationImport = (importId, data) =>
    api.post(`/location-imports/${importId}/approve`, data);
  const deleteLocationImport = id => api.delete(`/location-imports/${id}`);

  const changeMembranes = data => api.post('/locations/changeMembrane', data);

  const getCsvFiles = params => api.get('/locations/csv-files', params);

  const setDeviceId = params => api.post('/locations/set-device-id', params);

  const editLocation = (id, data) => api.put(`/locations/${id}`, data);

  const updateInstallationStatus = (id, data) => api.put(`/locations/update-status/${id}`, data);

  const startHealthCheckReport = params => api.post('/health-check/report/start', params);

  const startHealthCheckAttempt = reportId =>
    api.post(`/health-check/report/${reportId}/attempt/start`);

  const startHealthCheck = params => api.post('/health-check/start', params);

  const deleteLocation = id => api.delete(`/locations/${id}`);

  const getWaterFilterCurrentState = (serialNumber, data) =>
    consumptionApi.getWaterFilterCurrentState({ device_id: serialNumber, ...data });

  const getWaterFilterCurrentPressure = (serialNumber, data) =>
    consumptionApi.getWaterFilterCurrentPressure({ device_id: serialNumber, ...data });

  const getWaterFilterConsumption = params => consumptionApi.getWaterFilterConsumption(params);

  const getWaterFilterMaxFlowRate = params => consumptionApi.getWaterFilterMaxFlowRate(params);

  const getWaterFilterPressure = params => consumptionApi.getWaterFilterPressure(params);

  const getWaterFilterBypass = params => consumptionApi.getWaterFilterBypass(params);

  const getTotalConsumptionWaterFilter = params =>
    consumptionApi.getTotalConsumptionWaterFilter(params);

  const generateWaterFilterCsv = params => consumptionApi.exportCsv(params);

  const getWaterFilterCurrentBypass = (serialNumber, data) =>
    consumptionApi.getWaterFilterCurrentBypass({ device_id: serialNumber, ...data });

  const getTotalConsumptionCartridgeWf = params =>
    consumptionApi.getTotalConsumptionCartridgeWf(params);

  const getAllTotalConsumptionCartridgeWf = params =>
    consumptionApi.getAllTotalConsumptionCartridgeWf(params);

  const replaceWaterFilterCartridges = params => api.post('/wf/replace-cartridges', params);

  const getReverseOsmosisCurrentState = serialNumber =>
    consumptionApi.getReverseOsmosisCurrentState({ device_id: serialNumber });

  const getReverseOsmosisConsumption = params =>
    consumptionApi.getReverseOsmosisConsumption(params);

  const getReverseOsmosisWaste = params => consumptionApi.getReverseOsmosisWaste(params);

  const getReverseOsmosisTds = params => consumptionApi.getReverseOsmosisTds(params);

  const getTotalConsumptionReverseOsmosis = params =>
    consumptionApi.getTotalConsumptionReverseOsmosis(params);

  const generateReverseOsmosisCsv = params => consumptionApi.exportCsv(params);

  const getTotalConsumptionCartridgeRo = params =>
    consumptionApi.getTotalConsumptionCartridgeRo(params);

  const getAllTotalConsumptionCartridgeRo = params =>
    consumptionApi.getAllTotalConsumptionCartridgeRo(params);

  const replaceReverseOsmosisCartridges = params => api.post('/ro/replace-cartridges', params);

  const getReverseOsmosisBypass = params => consumptionApi.getReverseOsmosisBypass(params);

  const getReverseOsmosisCurrentBypass = serialNumber =>
    consumptionApi.getReverseOsmosisCurrentBypass({ device_id: serialNumber });

  const getReverseOsmosisCurrentPressure = (serialNumber, params) =>
    consumptionApi.getReverseOsmosisCurrentPressure({ device_id: serialNumber, ...params });

  const getReverseOsmosisCurrentTemperature = (serialNumber, params) =>
    consumptionApi.getReverseOsmosisCurrentTemperature({ device_id: serialNumber, ...params });

  const getSoftenerCurrentState = (serialNumber, params) =>
    consumptionApi.getSoftenerCurrentState({ device_id: serialNumber, ...params });

  const getSoftenerConsumption = params => consumptionApi.getSoftenerConsumption(params);

  const getSoftenerPressure = params => consumptionApi.getSoftenerPressure(params);

  const getSoftenerBypass = params => consumptionApi.getSoftenerBypass(params);

  const getSoftenerWaste = params => consumptionApi.getSoftenerWaste(params);

  const generateSoftenerCsv = params => consumptionApi.exportCsv(params);

  const getTotalConsumptionSoftener = params => consumptionApi.getTotalConsumptionSoftener(params);

  const getSoftenerCurrentPressure = (serialNumber, data) =>
    consumptionApi.getSoftenerCurrentPressure({ device_id: serialNumber, ...data });

  const getSoftenerCurrentBypass = (serialNumber, data) =>
    consumptionApi.getSoftenerCurrentBypass({ device_id: serialNumber, ...data });

  // library
  const getLibraries = params => api.get('/libraries', params);

  const getDeviceConfigurationUsage = params =>
    api.get('/libraries/device-configuration-usage', params);

  const deleteLibrary = (id, type) => api.delete(`/libraries/${id}/${type}`);

  const getCatridge = id => api.get(`/libraries/${id}/catridge`);

  const getCatridgesForFilter = () => api.get('/libraries/filter/catridge');

  const getWaterFiltersForFilter = () => api.get('/libraries/filter/water-filter');

  const getConfigurationsForFilter = type => api.get(`/libraries/filter/${type}`);

  const getReverseOsmosisMembraneForFilter = () =>
    api.get('/libraries/filter/reverse-osmosis-membrane');

  const getReverseOsmosisForFilter = () => api.get('/libraries/filter/reverse-osmosis-filter');

  const getSoftenerForFilter = () => api.get('/libraries/filter/softener');

  const getBrineDrumForFilter = () => api.get('/libraries/filter/brine-drum');

  const getWaterFilter = id => api.get(`/libraries/${id}/water-filter`);

  const getReverseOsmosisMembrane = id => api.get(`/libraries/${id}/reverse-osmosis-membrane`);

  const getReverseOsmosis = id => api.get(`/libraries/${id}/reverse-osmosis-filter`);

  const getSoftener = id => api.get(`/libraries/${id}/softener`);

  const getBrineDrum = id => api.get(`/libraries/${id}/brine-drum`);

  const getTanks = () => api.get('/libraries/filter/tanks');

  const getConfiguration = (id, type) => api.get(`/libraries/${id}/${type}`);

  const createWaterFilter = data => api.post('/libraries/water-filter', data);

  const editWaterFilter = (id, data) => api.put(`/libraries/${id}/water-filter`, data);

  const createReverseOsmosisMembrane = data =>
    api.post('/libraries/reverse-osmosis-membrane', data);

  const createSensor = (data, sensor_type) => api.post(`/libraries/${sensor_type}-sensor`, data);

  const getSensor = (id, sensor_type) => api.get(`/libraries/${id}/${sensor_type}`);

  const editSensor = (data, id, sensor_type) => api.put(`/libraries/${id}/${sensor_type}`, data);

  const getFlowSensorsForSelect = () => api.get('/libraries/flow-meter-sensor-select');

  const getPressureSensorsForSelect = () => api.get('/libraries/pressure-transducer-sensor-select');

  const getDistanceSensorsForSelect = () => api.get('/libraries/distance-sensor-select');

  const getTdsProbeSensorsForSelect = () => api.get('/libraries/tds-probe-sensor-select');

  const getTemperatureSensorsForSelect = () => api.get('/libraries/temperature-sensor-select');

  const editReverseOsmosisMembrane = (id, data) =>
    api.put(`/libraries/${id}/reverse-osmosis-membrane`, data);

  const createWaterCatridge = data => api.post('/libraries/catridge', data);

  const editWaterCatridge = (id, data) => api.put(`/libraries/${id}/catridge`, data);

  const createReverseOsmosis = data => api.post('/libraries/reverse-osmosis-filter', data);

  const editReverseOsmosis = (id, data) => api.put(`/libraries/${id}/reverse-osmosis-filter`, data);

  const createBrineDrum = data => api.post('/libraries/brine-drum', data);

  const editBrineDrum = (id, data) => api.put(`/libraries/${id}/brine-drum`, data);

  const createSoftener = data => api.post('/libraries/softener', data);

  const editSoftener = (id, data) => api.put(`/libraries/${id}/softener`, data);

  const createDeviceConfiguration = (data, deviceType) =>
    api.post(`/libraries/${deviceType}-configuration`, data);

  const editDeviceConfiguration = (data, id, deviceType) =>
    api.put(`/libraries/${id}/${deviceType}`, data);

  // Alerts
  const getAlerts = params => api.get('/alerts', params);
  const getAllAlerts = params => api.get('/alerts/all', params);
  const getAlert = id => api.get(`/alerts/${id}`);
  const getPendingCount = params => api.get('/alerts/pending-count', params);

  const markAlertAsViewed = params => api.post('/alerts/mark-viewed', params);

  const getAlertsTypes = params => api.get('/alert-types', params);

  const getAlertType = id => api.get(`/alert-types/${id}`);

  const createAlertType = data => api.post('/alert-types', data);

  const editAlertType = (id, data) => api.put(`/alert-types/${id}`, data);

  const deleteAlertType = id => api.delete(`/alert-types/${id}`);

  const getUsers = params => api.get('/users', params);

  const getUser = id => api.get(`/users/${id}`);

  const resendEmail = id => api.post(`/users/resend-email/${id}`);

  const getInstallersByAccount = accountId => api.get(`users/${accountId}/installers`);

  const createUser = data => api.post('/users', data);

  const editUser = (id, data) => api.put(`/users/${id}`, data);

  const deleteUser = id => api.delete(`/users/${id}`);

  const getAccounts = params => api.get('/accounts', params);

  const getAccount = id => api.get(`/accounts/${id}`);

  const getAccountsForFilter = params => api.get('/accounts/filter', params);

  const createAccount = data => api.post('/accounts', data);

  const editAccount = (id, data) => api.put(`/accounts/${id}`, data);

  const deleteAccount = id => api.delete(`/accounts/${id}`);

  const getBrands = params => api.get('/brands', params);

  const getBrand = id => api.get(`/brands/${id}`);

  const getBrandsForFilter = params => api.get('/brands/filter', params);

  const getBrandMetrics = params => api.get('/brands/metrics', params);

  const createBrand = data => api.post('/brands', data);

  const editBrand = (id, data) => api.put(`/brands/${id}`, data);

  const deleteBrand = id => api.delete(`/brands/${id}`);

  const getAdminsBrands = () => api.get('/brands/admin');

  const getAdminsAccounts = brandId => api.get(`brands/${brandId}/accounts/admin`);

  const getAdminsLocations = accountId => api.get(`account/${accountId}/locations/admin`);
  // Ota
  const getOtas = params => api.get('/ota/locations-updates', params);

  const getOta = id => api.get(`/ota/${id}`);

  const getOtaVersions = params => api.get('/ota/manage-version', params);

  const createOtaVersion = data => api.post('/ota/manage-version', data);

  const deleteOtaVersion = id => api.delete(`/ota/manage-version/${id}`);

  const getUpgradeStatus = params => api.get('/ota/ota-updates', params);

  const getUpgradeStatusDevices = params => api.get('/ota/ota-updates/get-devices', params);

  const createUpgradeStatus = data => api.post('/ota/ota-updates', data);

  const deleteUpgradeStatus = id => api.delete(`/ota/ota-updates/${id}`);

  const getPreviousInstallationInfo = data => api.get('/ota/previous-installation-info', data);

  const getUpdateActiveDevices = params => api.get('/ota/manage-version/active-devices', params);

  // Health checks (flat params like /locations so URL is ?limit=10&overall_status=passed)
  const getHealthChecks = params => api.get('/health-check', params);
  const getHealthCheckMetrics = params => api.get('/health-check/metrics', params);
  const getHealthCheckReport = reportId => api.get(`/health-check/report/${reportId}`);
  const getLastHealthCheckForLocation = locationId =>
    api.get(`/health-check/location/${locationId}/last`);

  // Roles, States, and Cities
  const getRoles = () => api.get('/roles');

  const getStates = () => api.get('/states');

  const getCitiesByState = id => api.get(`/states/${id}/cities`);

  // Support
  const getSupports = params => api.get('/supports', params);
  const deleteSupport = id => api.delete(`supports/${id}`);
  const checkAsRead = id => api.put(`/supports/${id}/check-as-read`);

  return {
    // api
    setAuthorization,
    removeAuthToken,
    // login
    localLogin,
    recoverPasswordRequest,
    resetPasswordRequest,
    newPasswordRequest,
    resendInvitation,
    // search
    getSearchSuggestions,
    // porfile
    updateProfile,
    changePassword,
    closeAccount,
    // library
    getLibraries,
    getDeviceConfigurationUsage,
    deleteLibrary,
    getCatridge,
    getCatridgesForFilter,
    getWaterFiltersForFilter,
    getConfigurationsForFilter,
    getReverseOsmosisMembraneForFilter,
    getReverseOsmosisForFilter,
    getSoftenerForFilter,
    getBrineDrumForFilter,
    getWaterFilter,
    getConfiguration,
    getReverseOsmosisMembrane,
    getReverseOsmosis,
    getSoftener,
    getBrineDrum,
    getTanks,
    createWaterFilter,
    editWaterFilter,
    createReverseOsmosisMembrane,
    createSensor,
    getSensor,
    editSensor,
    getFlowSensorsForSelect,
    getPressureSensorsForSelect,
    getDistanceSensorsForSelect,
    getTdsProbeSensorsForSelect,
    getTemperatureSensorsForSelect,
    editReverseOsmosisMembrane,
    createWaterCatridge,
    editWaterCatridge,
    createReverseOsmosis,
    editReverseOsmosis,
    createBrineDrum,
    editBrineDrum,
    createSoftener,
    editSoftener,
    createDeviceConfiguration,
    editDeviceConfiguration,
    // location
    getLocations,
    getLocation,
    getLocationsForFilter,
    createLocation,
    uploadLocationImport,
    getLocationImports,
    getLocationImport,
    updateLocationImportRow,
    approveLocationImport,
    deleteLocationImport,
    changeMembranes,
    getCsvFiles,
    setDeviceId,
    editLocation,
    deleteLocation,
    getWaterFilterCurrentState,
    getWaterFilterCurrentPressure,
    getWaterFilterConsumption,
    getWaterFilterMaxFlowRate,
    getWaterFilterPressure,
    getWaterFilterBypass,
    getTotalConsumptionWaterFilter,
    generateWaterFilterCsv,
    getWaterFilterCurrentBypass,
    getTotalConsumptionCartridgeWf,
    getAllTotalConsumptionCartridgeWf,
    replaceWaterFilterCartridges,
    getReverseOsmosisCurrentState,
    getReverseOsmosisConsumption,
    getTotalConsumptionReverseOsmosis,
    generateReverseOsmosisCsv,
    getReverseOsmosisTds,
    getReverseOsmosisWaste,
    getTotalConsumptionCartridgeRo,
    getAllTotalConsumptionCartridgeRo,
    replaceReverseOsmosisCartridges,
    getReverseOsmosisBypass,
    getReverseOsmosisCurrentBypass,
    getReverseOsmosisCurrentPressure,
    getReverseOsmosisCurrentTemperature,
    getSoftenerCurrentState,
    getSoftenerConsumption,
    getSoftenerPressure,
    getSoftenerBypass,
    getSoftenerWaste,
    generateSoftenerCsv,
    getTotalConsumptionSoftener,
    getSoftenerCurrentPressure,
    getSoftenerCurrentBypass,
    updateInstallationStatus,
    startHealthCheckReport,
    startHealthCheckAttempt,
    startHealthCheck,
    // Alerts
    getAlerts,
    getAllAlerts,
    getPendingCount,
    getAlert,
    getAlertsTypes,
    getAlertType,
    createAlertType,
    editAlertType,
    deleteAlertType,
    markAlertAsViewed,
    // user
    getUsers,
    getUser,
    resendEmail,
    getInstallersByAccount,
    createUser,
    editUser,
    deleteUser,
    // account
    getAccounts,
    getAccount,
    getAccountsForFilter,
    createAccount,
    editAccount,
    deleteAccount,
    // brand
    getBrands,
    getBrand,
    getBrandsForFilter,
    getBrandMetrics,
    createBrand,
    editBrand,
    deleteBrand,
    getAdminsBrands,
    getAdminsAccounts,
    getAdminsLocations,
    // Roles, States, and Cities
    getRoles,
    getStates,
    getCitiesByState,
    // Support
    getSupports,
    checkAsRead,
    deleteSupport,
    // Ota
    getOtas,
    getOta,
    getOtaVersions,
    createOtaVersion,
    deleteOtaVersion,
    getUpgradeStatus,
    getUpgradeStatusDevices,
    createUpgradeStatus,
    deleteUpgradeStatus,
    getPreviousInstallationInfo,
    getUpdateActiveDevices,
    // Health checks
    getHealthChecks,
    getHealthCheckMetrics,
    getHealthCheckReport,
    getLastHealthCheckForLocation,
  };
};

export default {
  create,
};
